import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileKey, Plus, Search, Eye, EyeOff, Copy, Check, Trash2 } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function LicensesModule() {
  const [licenses, setLicenses] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: '', software_name: '', license_key: '', seats: 1,
    expiry_date: '', vendor: '', cost: '', notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [licRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/licenses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (licRes.ok) setLicenses(await licRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowAddDialog(false);
        loadData();
      }
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleReveal = async (licId) => {
    if (revealedKeys[licId]) {
      setRevealedKeys(prev => ({ ...prev, [licId]: null }));
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/licenses/${licId}/reveal`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevealedKeys(prev => ({ ...prev, [licId]: data.license_key }));
      }
    } catch (err) {
      console.error('Failed to reveal:', err);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete license for ${name}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/licenses/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = licenses.filter(l =>
    l.software_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Software Licenses</h1>
          <p className="text-sm text-slate-500 mt-1">License key management</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" />Add License
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add Software License</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Software Name *</Label><Input value={formData.software_name} onChange={(e) => setFormData({...formData, software_name: e.target.value})} placeholder="Microsoft Office 365" required /></div>
                <div className="col-span-2"><Label>License Key *</Label><Input type="password" value={formData.license_key} onChange={(e) => setFormData({...formData, license_key: e.target.value})} required /></div>
                <div><Label>Seats</Label><Input type="number" value={formData.seats} onChange={(e) => setFormData({...formData, seats: e.target.value})} /></div>
                <div><Label>Vendor</Label><Input value={formData.vendor} onChange={(e) => setFormData({...formData, vendor: e.target.value})} /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={formData.expiry_date} onChange={(e) => setFormData({...formData, expiry_date: e.target.value})} /></div>
                <div><Label>Cost</Label><Input type="number" step="0.01" value={formData.cost} onChange={(e) => setFormData({...formData, cost: e.target.value})} placeholder="0.00" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">Add License</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search licenses..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><FileKey className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No licenses found</p></CardContent></Card> : (
        <div className="space-y-3">
          {filtered.map((lic) => (
            <Card key={lic.id} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileKey className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-800">{lic.software_name}</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><p className="text-slate-500">Organization</p><p className="font-medium">{lic.organization_name}</p></div>
                      <div><p className="text-slate-500">License Key</p><div className="flex items-center gap-2"><code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{revealedKeys[lic.id] || '••••-••••-••••-••••'}</code><Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleReveal(lic.id)}>{revealedKeys[lic.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</Button>{revealedKeys[lic.id] && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleCopy(revealedKeys[lic.id], lic.id)}>{copiedId === lic.id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}</Button>}</div></div>
                      {lic.seats && <div><p className="text-slate-500">Seats</p><p className="font-medium">{lic.seats}</p></div>}
                      {lic.expiry_date && <div><p className="text-slate-500">Expires</p><p className="font-medium">{new Date(lic.expiry_date).toLocaleDateString()}</p></div>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(lic.id, lic.software_name)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
